import { Dialog, DialogContent, DialogTitle, IconButton, Typography, Box, useTheme, alpha, Fade, Button, Divider, List, ListItem, ListItemText } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import React, { useEffect, useRef } from "react";

const SECTION_MAP = { masterAgreement: "1. Services Offered", termsOfUse: "2. Eligibility", privacyPolicy: "4. Commitment Fees" };
const liSx = { display: "list-item", listStyleType: "disc", pl: 1 };
const h6Sx = { gutterBottom: true, fontWeight: 600 };
const divSx = { my: 2 };

const Section = ({ title, children }) => (
  <>
    <Typography variant="h6" {...h6Sx}>{title}</Typography>
    {children}
    <Divider sx={divSx} />
  </>
);

const TermsOfServiceContent = () => (
  <Box>
    <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>Last Updated: 28th March 2025</Typography>
    <Typography variant="body1" paragraph>
      Welcome to Smyan Tech Solutions Pvt Ltd (herein referred as "dequitec", "we," "us," or "our"). These Terms of Service ("Terms") govern your access to and use of our website, www.dequitec.in (the "Site"), and the services we provide, including business loan aggregation, credit analysis report downloads, one-pager generation, and CMA Data/financial projection tools (collectively, the "Services"). By accessing or using the Site and Services, you agree to be bound by these Terms. If you do not agree with any part of these Terms, you must not use the Site or Services.
    </Typography>
    <Divider sx={divSx} />
    <Section title="1. Acceptance of Terms">
      <Typography variant="body1" paragraph>
        By using the Site and Services, you represent that you are at least 18 years old and have the legal capacity to enter into these Terms. If you are using the Services on behalf of a business, you represent that you have the authority to bind that business to these Terms.
      </Typography>
    </Section>
    <Section title="2. Description of Services">
      <Typography variant="body1">Our Services include:</Typography>
      <List dense disablePadding>
        {[
          ["Business Loan Aggregation:", "Connecting you with Lenders who may offer business loan options based on the information you provide."],
          ["Credit Analysis Downloads:", "Providing access to your business and/or personal credit analysis reports derived from third-party data."],
          ["One-Pager Generation:", "Offering tools to generate a concise summary of your business's key financial and operational information."],
          ["Financial Projection Tools:", "Offering tools to assist you in creating financial projections for your business."]
        ].map(([label, text], i) => (
          <ListItem key={i} sx={liSx}><Typography variant="body1"><strong>{label}</strong> {text}</Typography></ListItem>
        ))}
      </List>
    </Section>
    <Section title="3. User Accounts">
      <List dense disablePadding>
        {[
          "To access certain features of the Services, you may be required to create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.",
          "You agree to provide accurate and complete information when creating your account and using the Services.",
          "We reserve the right to suspend or terminate your account if we believe you have violated these Terms."
        ].map((text, i) => (
          <ListItem key={i} sx={liSx}><Typography variant="body1">{text}</Typography></ListItem>
        ))}
      </List>
    </Section>
    <Section title="4. Credit Analysis, One-Pagers, and Financial Projections">
      <List dense disablePadding>
        {[
          ["Credit Analysis:", "Reports are derived from third-party data and may have limitations. We do not guarantee accuracy."],
          ["One-Pagers:", "Generated based on the information you provide. Accuracy is your responsibility."],
          ["Financial Projections:", "Provided for informational purposes only and should not be considered financial advice."]
        ].map(([label, text], i) => (
          <ListItem key={i} sx={liSx}><Typography variant="body1"><strong>{label}</strong> {text}</Typography></ListItem>
        ))}
      </List>
    </Section>
    <Typography variant="h6" {...h6Sx}>5. Loan Aggregation and Lender Interactions</Typography>
    <Typography variant="body1" paragraph>We act as an intermediary between you and Lenders. We do not guarantee that you will be approved for a loan. Loan offers are subject to the terms and conditions of the Lenders.</Typography>
    <Divider sx={divSx} />
    <Typography variant="h6" {...h6Sx}>6. User Content</Typography>
    <Typography variant="body1" paragraph>You are responsible for any information, data, or content you provide through the Services.</Typography>
    <Divider sx={divSx} />
    <Typography variant="h6" {...h6Sx}>7. Intellectual Property</Typography>
    <Typography variant="body1" paragraph>The Site and Services, including all content, software, and trademarks, are owned by us or our licensors and are protected by intellectual property laws.</Typography>
    <Divider sx={divSx} />
    <Typography variant="h6" {...h6Sx}>8. Disclaimer of Warranties</Typography>
    <Typography variant="body1" paragraph>The Services are provided "as is" without warranties of any kind, whether express or implied.</Typography>
    <Divider sx={divSx} />
    <Typography variant="h6" {...h6Sx}>9. Limitation of Liability</Typography>
    <Typography variant="body1" paragraph>We shall not be liable for any indirect, incidental, consequential, or punitive damages arising from your use of the Services.</Typography>
    <Divider sx={divSx} />
    <Typography variant="h6" {...h6Sx}>10. Indemnification</Typography>
    <Typography variant="body1" paragraph>You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from your use of the Services or your violation of these Terms.</Typography>
    <Divider sx={divSx} />
    <Typography variant="h6" {...h6Sx}>11. Termination</Typography>
    <Typography variant="body1" paragraph>We may terminate your access to the Services at any time, with or without cause.</Typography>
    <Divider sx={divSx} />
    <Typography variant="h6" {...h6Sx}>12. Governing Law and Dispute Resolution</Typography>
    <Typography variant="body1" paragraph>These Terms shall be governed by the laws of India, with disputes resolved in Delhi courts.</Typography>
    <Divider sx={divSx} />
    <Typography variant="h6" {...h6Sx}>13. Changes to these Terms</Typography>
    <Typography variant="body1" paragraph>We may update these Terms periodically. Your continued use of the Services after updates constitutes your acceptance.</Typography>
    <Divider sx={divSx} />
    <Typography variant="h6" {...h6Sx}>14. Contact Us</Typography>
    <Typography variant="body1">Smyan Tech Solutions Pvt Ltd, Bldg No. 145, Sector 44, Gurgaon, Haryana – 122003; contact@dequitec.com</Typography>
  </Box>
);

const PRIVACY_LIST_ITEMS = [
  { primary: "• Information You Provide Directly", secondary: <>o When you complete a loan inquiry form, we collect information such as your business name, contact information (name, email address, phone number), business address, business financial information (revenue, years in business), loan amount requested, and other relevant details.<br /><br />o If you contact us directly (e.g., via email or phone), we may collect the information you provide during the communication.</> },
  { primary: "• Information Collected Automatically", secondary: <>o We may automatically collect certain information about your use of the Site, including your IP address, browser type, operating system, referring URLs, pages visited, and the dates and times of your visits.<br /><br />o We may use cookies, web beacons, and similar technologies to collect this information. You can control cookies through your browser settings, but disabling cookies may affect your ability to use certain features of the Site.</> },
  { primary: "• Information from Third Parties", secondary: "o We may receive information about you from Lenders or other third-party service providers, such as credit bureaus or data aggregators, to verify your information or improve our services." }
];

const PRIVACY_USE_ITEMS = ["• Connecting you with Lenders who may provide business loan options.", "• Verifying your information and assessing your eligibility for loans.", "• Improving the Site and our services.", "• Responding to your inquiries and providing customer support.", "• Sending marketing and promotional materials (opt-out anytime).", "• Preventing fraud and ensuring site security.", "• Complying with legal obligations."];

const PRIVACY_SHARE_ITEMS = ["• Lenders: We share loan inquiry details with lenders to provide loan options.", "• Third-Party Service Providers: We may share data with hosting, analytics, and marketing service providers.", "• Credit Bureaus: Your data may be shared for verification and creditworthiness assessment.", "• Legal Authorities: We may disclose data when required by law.", "• Business Transfers: In case of merger/acquisition, your data may be transferred."];

const PrivacyPolicyContent = () => (
  <Box>
    <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>Last Updated: 28th Mar 2025</Typography>
    <Typography variant="body1" paragraph>
      This Privacy Policy describes how Smyan Tech Solutions Pvt Ltd (herein "Dequitec", "we," "us," or "our") collects, uses, and shares information about you when you use our website, www.dequitec.com (the "Site"), to analyze, compare, and connect with business loan providers ("Lenders"). By using the Site, you agree to the terms of this Privacy Policy.
    </Typography>
    <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }} color="primary">1. Information We Collect</Typography>
    <List dense>
      {PRIVACY_LIST_ITEMS.map((item, i) => <ListItem key={i}><ListItemText {...item} /></ListItem>)}
    </List>
    <Divider sx={divSx} />
    <Typography variant="h6" fontWeight={600} color="primary">2. How We Use Your Information</Typography>
    <List dense>{PRIVACY_USE_ITEMS.map((text, i) => <ListItem key={i}><ListItemText primary={text} /></ListItem>)}</List>
    <Divider sx={divSx} />
    <Typography variant="h6" fontWeight={600} color="primary">3. Sharing Your Information</Typography>
    <List dense>{PRIVACY_SHARE_ITEMS.map((text, i) => <ListItem key={i}><ListItemText primary={text} /></ListItem>)}</List>
    <Divider sx={divSx} />
    <Typography variant="h6" fontWeight={600} color="primary">4. Data Security</Typography>
    <Typography variant="body1" paragraph>We take reasonable measures to protect your information from unauthorized access, use, or disclosure. However, no method of transmission over the internet or electronic storage is completely secure, so we cannot guarantee absolute security.</Typography>
    <Typography variant="h6" fontWeight={600} color="primary">5. Data Retention</Typography>
    <Typography variant="body1" paragraph>We retain your information for as long as necessary to fulfill the purposes described in this Privacy Policy, unless a longer retention period is required or permitted by law.</Typography>
    <Divider sx={divSx} />
    <Typography variant="h6" fontWeight={600} color="primary">6. Your Rights</Typography>
    <Typography variant="body1" paragraph>Depending on your location, you may have rights to access, modify, delete, or restrict your data. You may also opt out of marketing communications.</Typography>
    <Typography variant="h6" fontWeight={600} color="primary">7. Children's Privacy</Typography>
    <Typography variant="body1" paragraph>Our Site is not intended for children under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected personal information from a child under 18, we will take steps to delete it.</Typography>
    <Typography variant="h6" fontWeight={600} color="primary">8. Changes to this Privacy Policy</Typography>
    <Typography variant="body1" paragraph>We may update this Privacy Policy from time to time. We will post any changes on this page and update the "Last Updated" date. We encourage you to review this Privacy Policy periodically.</Typography>
    <Typography variant="h6" fontWeight={600} color="primary">9. Contact Us</Typography>
    <Typography variant="body1" paragraph>If you have any questions, reach out to us at:</Typography>
    <Typography variant="body1" paragraph><strong>Smyan Tech Solutions Pvt Ltd</strong><br />Bldg no. 145, Sector 44, Gurgaon, Haryana – 122003</Typography>
    <Typography variant="body1" paragraph><strong>Email: contact@dequitec.com</strong></Typography>
  </Box>
);

const CONTENT_MAP = { termsOfService: TermsOfServiceContent, privacyPolicy: PrivacyPolicyContent };

const TermsDialog = ({ open, onClose, content, highlightSection, showAgreeButton = false, onAgree, title = "Terms and Conditions", contentType }) => {
  const contentRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    if (open && highlightSection && contentRef.current) {
      const targetText = SECTION_MAP[highlightSection];
      if (targetText) {
        const textContent = contentRef.current.textContent;
        const startIndex = textContent.indexOf(targetText);
        if (startIndex !== -1) {
          const range = document.createRange();
          const textNodes = [];
          contentRef.current.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
              textNodes.push(node);
            }
          });

          let currentPos = 0;
          for (let i = 0; i < textNodes.length; i++) {
            const node = textNodes[i];
            if (currentPos + node.length > startIndex) {
              range.setStart(node, startIndex - currentPos);
              break;
            }
            currentPos += node.length;
          }

          range.collapse(true);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);

          range.startContainer.parentElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }
    }
  }, [open, highlightSection]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      TransitionComponent={Fade}
      transitionDuration={400}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: "16px",
          maxHeight: "90vh",
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0px 8px 40px rgba(0, 0, 0, 0.12)',
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          p: 4,
          pb: 3,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          backgroundColor: theme.palette.background.paper,
          position: 'relative'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2
            }}
          >
            <ArticleOutlinedIcon 
              sx={{ 
                color: theme.palette.primary.main,
                fontSize: '24px'
              }} 
            />
          </Box>
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.primary,
                letterSpacing: '-0.01em',
                mb: 0.5
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                lineHeight: 1.5
              }}
            >
              Please read these terms carefully before proceeding
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={onClose} 
          size="small"
          sx={{
            position: 'absolute',
            right: 24,
            top: 24,
            backgroundColor: alpha(theme.palette.divider, 0.04),
            backdropFilter: 'blur(4px)',
            '&:hover': {
              backgroundColor: alpha(theme.palette.divider, 0.08),
              transform: 'scale(1.05)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent 
        sx={{ 
          p: 0,
          '&::-webkit-scrollbar': {
            width: '6px',
            backgroundColor: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.divider, 0.15),
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: alpha(theme.palette.divider, 0.25)
            }
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent'
          }
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            p: 4,
            pb: showAgreeButton ? 2 : 4,
            color: alpha(theme.palette.text.primary, 0.85),
            fontSize: '0.9375rem',
            lineHeight: 1.75,
            '& h1, & h2, & h3, & h6': {
              color: theme.palette.text.primary,
              fontWeight: 600,
              mb: 2.5,
              letterSpacing: '-0.02em'
            }
          }}
        >
          {CONTENT_MAP[contentType] ? React.createElement(CONTENT_MAP[contentType]) : (
            <Typography
              component="div"
              sx={{
                '& p': { mb: 2.5, position: 'relative', '&:hover': { color: theme.palette.text.primary, transition: 'color 0.2s ease-in-out' } },
                '& ul, & ol': { mt: -1, mb: 2.5, pl: 3 },
                '& li': { mb: 1.5, position: 'relative', '&::before': { content: '""', position: 'absolute', left: -20, top: '50%', transform: 'translateY(-50%)', width: 6, height: 6, borderRadius: '50%', backgroundColor: alpha(theme.palette.primary.main, 0.5) } },
                '& strong': { color: theme.palette.text.primary, fontWeight: 500 }
              }}
            >
              {content.split('\n\n').map((paragraph, index) => (
                <Typography key={index} component="p" sx={{ mb: 2.5, '&:last-child': { mb: 0 } }}>
                  {paragraph}
                </Typography>
              ))}
            </Typography>
          )}
        </Box>
        {showAgreeButton && (
          <Box
            sx={{
              p: 3,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.5),
              backdropFilter: 'blur(8px)'
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                onAgree?.();
                onClose();
              }}
              sx={{
                px: 4,
                py: 1,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9375rem',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:hover': {
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              I Agree
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TermsDialog; 